import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { WATCH_SHORTCUT_GROUPS, type ShortcutGroup } from "@/lib/shortcuts";
import { cn } from "@/lib/utils";

/**
 * Keyboard-shortcuts help panel (Frame.io-style). Left category rail + search +
 * grouped rows with key chips. Data comes from lib/shortcuts.ts so this stays in
 * sync with the real handlers. Opened with `?` from the watch page.
 */
export function ShortcutsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [query, setQuery] = useState("");
  const [activeTitle, setActiveTitle] = useState<string | null>(null);

  // Filter by search text (label or key) first, then by the selected category.
  const groups = useMemo<ShortcutGroup[]>(() => {
    const q = query.trim().toLowerCase();
    return WATCH_SHORTCUT_GROUPS.map((g) => {
      const items = q
        ? g.items.filter(
            (it) =>
              it.label.toLowerCase().includes(q) ||
              it.keys.some((k) => k.toLowerCase().includes(q)),
          )
        : g.items;
      return { ...g, items };
    }).filter((g) => g.items.length > 0 && (!activeTitle || g.title === activeTitle));
  }, [query, activeTitle]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
        <div className="border-b border-border px-5 py-4">
          <DialogTitle className="text-base font-semibold">Keyboard Shortcuts</DialogTitle>
        </div>

        <div className="flex max-h-[60vh] min-h-[22rem]">
          {/* Left category rail */}
          <div className="w-44 shrink-0 border-r border-border bg-secondary/30 p-2">
            <RailButton active={!activeTitle} onClick={() => setActiveTitle(null)}>
              All
            </RailButton>
            {WATCH_SHORTCUT_GROUPS.map((g) => (
              <RailButton
                key={g.title}
                active={activeTitle === g.title}
                onClick={() => setActiveTitle(g.title)}
              >
                {g.title}
              </RailButton>
            ))}
          </div>

          {/* Right: search + grouped rows */}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-border p-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search keyboard shortcuts…"
                  className="w-full rounded-md border border-border bg-background py-2 pl-8 pr-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {groups.length === 0 ? (
                <div className="grid h-full place-items-center text-sm text-muted-foreground">
                  No shortcuts match “{query}”.
                </div>
              ) : (
                groups.map((g) => (
                  <section key={g.title} className="mb-4 last:mb-0">
                    <h3 className="mb-1.5 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {g.title}
                    </h3>
                    <div className="overflow-hidden rounded-lg border border-border">
                      {g.items.map((it, i) => (
                        <div
                          key={`${it.label}-${i}`}
                          className={cn(
                            "flex items-center justify-between gap-3 px-3 py-2.5 text-sm",
                            i > 0 && "border-t border-border",
                          )}
                        >
                          <span className="min-w-0 text-foreground">{it.label}</span>
                          <span className="flex shrink-0 items-center gap-1">
                            {it.keys.map((k, ki) => (
                              <Kbd key={ki}>{k}</Kbd>
                            ))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>
                ))
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RailButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
        active
          ? "bg-background font-medium text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-grid h-6 min-w-[1.6rem] place-items-center rounded-md border border-border bg-secondary px-1.5 font-sans text-xs font-medium text-foreground shadow-sm">
      {children}
    </kbd>
  );
}
